import { TabContext, TabList, TabPanel } from "@mui/lab";
import type { SxProps, Theme } from "@mui/material";
import { Box, Container, Tab } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { DocumentCollectionOperations, DocumentIdentifier } from "../../../../api/types/documentCollectionTypes";
import useClientContext from "../../../../context/useClientContext";
import ChangeDocumentAccessDialog from "../ChangeDocumentAccessDialog";
import ChangeDocumentCollectionsStatusDialog from "../ChangeDocumentCollectionsStatusDialog";
import { DocumentCollectionContextProvider } from "./DocumentCollectionContext";
import DocumentCollectionHeader from "./DocumentCollectionHeader";
import type { DocumentAccessOption } from "./documents-view/documentAccessHelper";
import DocumentsViewFundLevel from "./documents-view/DocumentsViewFundLevel";
import DocumentsViewInvestorLevel from "./documents-view/DocumentsViewInvestorLevel";
import EmailsView from "./emails-view/EmailsView";

const tabPanelStyle: SxProps<Theme> = () => ({
  height: "100%",
  p: 0,
});

interface ChangeStatusDialogState {
  operation: DocumentCollectionOperations;
  selectedIds: string[];
  documentsInfo: string;
  level: "document_collections" | "documents";
}

interface ChangeAccessDialogState {
  selectedDocuments: DocumentIdentifier[];
  currentDocumentsAccess: DocumentAccessOption[];
}

const DocumentCollectionPage = () => {
  usePageTitle("Document Collection");

  const { id } = useParams();
  const { hasPermissions, investorPortalApp } = useClientContext();

  const fetchDocumentCollectionDetails = useCallback(() => adminApi.getDocumentCollectionDetails(defined(id)), [id]);
  const [documentCollectionDetails, fetchError, { isFetching, fetch }] = useFetch(fetchDocumentCollectionDetails);

  const [sentInvites, setSentInvites] = useState<Record<string, Date>>({});
  const [selectedTab, setSelectedTab] = useState("Documents");
  const [changeStatusDialog, setChangeStatusDialog] = useState<ChangeStatusDialogState>();
  const [changeAccessDialog, setChangeAccessDialog] = useState<ChangeAccessDialogState>();

  if (fetchError) {
    if (fetchError.type === "NotFound") {
      return <Navigate to=".." replace />;
    }

    logError(fetchError, "[DocumentCollection]");
    return <DataLoadingFailed title="Loading documents failed" />;
  }

  if (!documentCollectionDetails || isFetching) {
    return <InlineLoader />;
  }

  const handleChangeDocumentCollectionStatus = (operation: DocumentCollectionOperations) => {
    setChangeStatusDialog({
      level: "document_collections",
      operation,
      selectedIds: [documentCollectionDetails.id],
      documentsInfo: documentCollectionDetails.name,
    });
  };

  const handleChangeDocumentsStatus = (
    operation: DocumentCollectionOperations,
    postMessageRequestIds: string[],
    documentsInfo: string
  ) => {
    setChangeStatusDialog({
      level: "documents",
      operation,
      selectedIds: postMessageRequestIds,
      documentsInfo,
    });
  };

  const handleChangeDocumentsAccess = (
    documents: DocumentIdentifier[],
    currentDocumentsAccess: DocumentAccessOption[]
  ) => {
    setChangeAccessDialog({
      selectedDocuments: documents,
      currentDocumentsAccess,
    });
  };

  const investorPortalEnabled = Boolean(investorPortalApp);
  const showPublishingStatus = documentCollectionDetails.status === "PartiallyLive";
  const showPortalPublishStatus =
    investorPortalEnabled && documentCollectionDetails.portalPublishStatus === "PartiallyPublished";
  const showFundName = documentCollectionDetails.funds.length > 1;

  return (
    <DocumentCollectionContextProvider
      documentCollection={documentCollectionDetails}
      hasPermissionsToManage={hasPermissions(["ManageDocumentCollections"])}
      investorPortalEnabled={investorPortalEnabled}
      sentInvites={sentInvites}
      setSentInvites={setSentInvites}
      onChangeDocumentCollectionStatus={handleChangeDocumentCollectionStatus}
      onChangeDocumentsStatus={handleChangeDocumentsStatus}
      onChangeDocumentsAccess={handleChangeDocumentsAccess}
    >
      <Container maxWidth={false} sx={{ height: "100%", py: 1.5, px: 3, overflowY: "hidden" }}>
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
          {documentCollectionDetails && (
            <DocumentCollectionHeader documentCollectionDetails={documentCollectionDetails} />
          )}
          <TabContext value={selectedTab}>
            <TabList onChange={(_, tab: string) => setSelectedTab(tab)}>
              <Tab label="Documents" value="Documents" />
              {documentCollectionDetails.showEmailsView && <Tab label="Emails" value="Emails" />}
            </TabList>
            <TabPanel value="Documents" sx={tabPanelStyle}>
              {documentCollectionDetails.messageLevel === "Fund" && (
                <DocumentsViewFundLevel
                  documents={documentCollectionDetails.documents}
                  showPublishingStatus={showPublishingStatus}
                  showPortalPublishStatus={showPortalPublishStatus}
                  investorPortalEnabled={investorPortalEnabled}
                  showFundName={showFundName}
                />
              )}
              {documentCollectionDetails.messageLevel !== "Fund" && (
                <DocumentsViewInvestorLevel
                  documents={documentCollectionDetails.documents}
                  showPublishingStatus={showPublishingStatus}
                  showPortalPublishStatus={showPortalPublishStatus}
                  investorPortalEnabled={investorPortalEnabled}
                  showFundName={showFundName}
                />
              )}
            </TabPanel>
            {documentCollectionDetails.showEmailsView && (
              <TabPanel value="Emails" sx={tabPanelStyle}>
                <EmailsView documentCollectionId={defined(id)} />
              </TabPanel>
            )}
          </TabContext>
        </Box>

        {changeStatusDialog && (
          <ChangeDocumentCollectionsStatusDialog
            operationLevel={changeStatusDialog.level}
            selectedIds={changeStatusDialog.selectedIds}
            operation={changeStatusDialog.operation}
            documentsInfo={changeStatusDialog.documentsInfo}
            onApply={fetch}
            onClose={() => setChangeStatusDialog(undefined)}
          />
        )}

        {changeAccessDialog && (
          <ChangeDocumentAccessDialog
            selectedDocuments={changeAccessDialog.selectedDocuments}
            currentDocumentsAccess={changeAccessDialog.currentDocumentsAccess}
            onApply={fetch}
            onClose={() => setChangeAccessDialog(undefined)}
          />
        )}
      </Container>
    </DocumentCollectionContextProvider>
  );
};

export default DocumentCollectionPage;
